import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { fieldTypes, FormData, FormProviderService, PreventDoubleSubmission } from '@fry/lib/forms';
import { RolesStore } from '@fry/lib/roles';
import { UserField, UserFieldsStore } from '@fry/lib/user-fields';
import { iCaseCompare } from '@fry/lib/utils';
import { Observable, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable()
export class UserFieldForm {

    public get pristine() {
        if (!this.formData) { return true; }
        return this.formData.pristine;
    }

    private formData: FormData;

    private fields = [
        {
            id: 'name',
            title: $localize `Name`,
            initial: '',
            widget: {
                type: 'string',
                autocomplete: 'on'
            },
            options: { required: true },
            validators: [Validators.required]
        },
        {
            id: 'fieldType',
            title: $localize `User field type`,
            initial: 'string',
            widget: {
                type: 'select',
                autocomplete: 'on'
            },
            validators: [Validators.required],
            options: {
                required: true,
                categories: Object.entries(fieldTypes).map(entry => ({
                    id: entry[0],
                    name: entry[1]
                }))
            }
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => {
                return parent.fieldType === 'select';
            },
            validators: [],
            options: { options: { allowsHierarchy: false }}
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'multiSelect',
            validators: [],
            options: { options: { allowsHierarchy: false }}
        },
        {
            id: 'categories',
            title: $localize `User field categories`,
            initial: '',
            widget: {
                type: 'treeEditorFormControl',
                autocomplete: 'off'
            },
            showCondition: (_value: any, parent: any, _doc: any) => parent.fieldType === 'hierarchy',
            validators: [],
            options: { options: { allowsHierarchy: true }}
        },
        // Hidden until we implement properly user list custom display
        // {
        //     id: 'isFeatured',
        //     title: 'Show this field on the users list?',
        //     initial: false,
        //     widget: {
        //         type: 'boolean',
        //     },
        //     validators: []
        // },
        {
            id: 'isRequired',
            title: $localize `Make this a required field?`,
            initial: false,
            widget: {
                type: 'boolean',
            },
            validators: []
        },
        // {
        //     id: 'isUnique',
        //     title: 'Make this a unique field?',
        //     initial: false,
        //     widget: {
        //         type: 'boolean',
        //     },
        //     validators: []
        // },
        {
            id: 'isLocked',
            title: $localize `Lock this field (non editable by users)?`,
            initial: false,
            widget: {
                type: 'boolean',
            },
            validators: []
        },
    ];

    constructor(
        private provider: FormProviderService,
        private userFields: UserFieldsStore,
        private roles: RolesStore,
    ) {}

    init(userField: UserField): Observable<FormData> {
        const model = userField.isNew() ? userField.doc : userField.transformed;
        return this.roles.all().pipe(
            switchMap(roles => {
                const roleOpts = roles
                    .map(role => {
                        return {
                            id: role.id,
                            name: role.doc.title
                        };
                    })
                    .sort((a, b) => iCaseCompare(a.name, b.name));

                const fields = [
                    ...this.fields,
                    {
                        id: 'visibleBy',
                        title: $localize `Who can see this user field?`,
                        initial: ['__all__'],
                        widget: {
                            type: 'multiSelect',
                            autocomplete: 'on'
                        },
                        validators: [Validators.required],
                        options: {
                            categories: [
                                { id: '__all__', name: $localize `All roles`},
                                { id: '__owner__', name: $localize `Owner`},
                                ...roleOpts
                            ]
                        }
                    }
                ];
                return this.provider.getFormDataWith(fields, model);
            }),
            tap(formData => this.formData = formData)
        )
    }

    @PreventDoubleSubmission()
    submit(userField: UserField): Observable<UserField> {
        if (this.formData.invalid) {
            return throwError($localize `Please review the form`);
        }

        // TODO: we need to figure out how we save doc vs orig
        // const value = {...userField.doc, ...this.formData.getValue()};
        // userField.doc = value;
        // Object.assign(userField.doc, this.formData.getValue());
        userField.setValue(this.formData.getValue());
        return this.userFields.save(userField);
    }
}
